import * as React from "react"
import { connect } from "react-redux"

import debounce from "lodash.debounce"
import { ILocation } from "../../interfaces/location"
import { breakpoints } from "../../modules/screen"
import CustomError from "../ErrorMessage/CustomError"
import ErrorMessage from "../ErrorMessage"
import Where2goMap from "../Where2goMap"
import { CustomErrorSignificance } from "../ErrorMessage/Types/CustomErrorSignificance"
import { LngLatLike } from "mapbox-gl"
import { IWhere2GoData } from "../../interfaces/where2go"

declare const mb: { where2go: { userLocation: ILocation } }

interface IProps {
    date: number,
    period: number,
    radius: number,
}

interface IState {
    mapCenter: LngLatLike
    markerPos: LngLatLike
    isMobile: boolean
    data: IWhere2GoData | null
    hasErrored: boolean
    error: Error | null
}

/**
 * Where2Go Class
 */
class Where2Go extends React.PureComponent<IProps, IState> {
    private onResize = debounce(() => {
        const isMobile = window.innerWidth < breakpoints.DESKTOP
        this.setState({
            isMobile,
        })
    }, 100)

    public constructor(props: IProps) {
        super(props)
        const userLocation = mb.where2go.userLocation
        const lat = userLocation.lat
        const lng = userLocation.lon

        this.state = {
            isMobile: window.innerWidth < breakpoints.DESKTOP,
            mapCenter: {
                lat,
                lng,
            },
            markerPos: {
                lat,
                lng,
            },
            data: null,
            hasErrored: false,
            error: null,
        }
    }

    public componentDidCatch = (error: Error) => {
        this.setState({
            error,
            hasErrored: true,
        })
    }

    public componentDidMount = () => {
        window.addEventListener("resize", this.onResize)
        this.update()
    }

    public componentWillUnmount = () => {
        window.removeEventListener("resize", this.onResize)
    }

    public componentDidUpdate = (prevProps: IProps) => {
        if (this.props.date !== prevProps.date
            || this.props.period !== prevProps.period
            || this.props.radius !== prevProps.radius
        ) {
            this.update()
        }
    }

    public render = () => {
        const userLocation = mb.where2go.userLocation

        return (
            <>
                {
                    this.state.hasErrored ? (
                        <ErrorMessage
                            error={new CustomError(this.state.error.message, CustomErrorSignificance.Fatal)}
                            retryAction={this.retryAction}
                        />
                    ) : (
                            <Where2goMap
                                mapCenter={this.state.mapCenter}
                                markerPos={this.state.markerPos}
                                userLocation={userLocation}
                                data={this.state.data}
                                isMobile={this.state.isMobile}
                            />
                        )
                }
            </>
        )
    }

    private update = () => {
        const userLocation = mb.where2go.userLocation
        const { date, period, radius } = this.props

        const ts = Date.now()
        const requestedTs = ts + date * 86400000
        const requestedDate = new Date(requestedTs)
        const dateString = `${requestedDate.getFullYear()}${
            ('0' + (requestedDate.getMonth() + 1)).slice(-2)}${
            ('0' + requestedDate.getDate()).slice(-2)}`

        const url = `/weather/outdoorsports/where2go/${userLocation.url}?date=${dateString}&period=${period}&radius=${radius}`

        const fetchOptions: RequestInit = {
            headers: { "X-Requested-With": "XMLHttpRequest" }
        }

        fetch(url, fetchOptions)
            .then((response: Response): object => {
                return response.json()
                    .then((json) => {
                        if (!response.ok) {
                            const customError = CustomError.fromObject(json, false)
                            throw customError
                        }
                        return json
                    }).catch(() => {
                        throw CustomError.withHttpError(
                            response.statusText,
                            response.status,
                            response.url,
                        )
                    })
            })
            .then((data: IWhere2GoData) => {
                this.setState({
                    data
                })
            })
            .catch((error) => {
                this.setState({
                    hasErrored: true,
                    error,
                })
            })

        this.setState({
            mapCenter: { lat: userLocation.lat, lng: userLocation.lon },
            markerPos: { lat: userLocation.lat, lng: userLocation.lon },
        })
    }

    private retryAction = () => {
        this.setState({
            hasErrored: false,
            error: null,
        })
        this.update()
    }
}

const mapStateToProps = (state) => {
    return {
        date: state.where2go.date,
        period: state.where2go.period,
        radius: state.where2go.radius,
    }
}

export default connect<IProps, {}>(
    mapStateToProps,
    {},
)(Where2Go)
